import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Rating,
  Typography,
} from '@mui/material';
import { AppointmentResponseDto, AppointmentStatus } from '../../../../types/appointment.types';
import { UserAvatar } from '../../../common/UserAvatar';
import { BaseDialog } from '../../../common/BaseDialog';
import { ModalIndices } from '../../../../common/i-doc/i-doc.registry';
import { AppointmentCountdown } from '../../AppointmentCountdown';

const BASE_STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]:   'Очікує підтвердження',
  [AppointmentStatus.CONFIRMED]: 'Підтверджено',
  [AppointmentStatus.CANCELLED]: 'Скасовано',
  [AppointmentStatus.COMPLETED]: 'Завершено',
};

function getClientStatusLabel(a: AppointmentResponseDto): string {
  if (a.status === AppointmentStatus.CANCELLED) {
    if (a.cancelledBy === 'client') return 'Скасовано вами';
    if (a.cancelledBy === 'expert') return 'Скасовано експертом';
  }
  return BASE_STATUS_LABELS[a.status];
}

const STATUS_COLORS: Record<AppointmentStatus, 'warning' | 'success' | 'error' | 'default'> = {
  [AppointmentStatus.PENDING]:   'warning',
  [AppointmentStatus.CONFIRMED]: 'success',
  [AppointmentStatus.CANCELLED]: 'error',
  [AppointmentStatus.COMPLETED]: 'default',
};

const canCancel = (s: AppointmentStatus) => s === AppointmentStatus.PENDING || s === AppointmentStatus.CONFIRMED;
// E1: reschedule дозволено ЛИШЕ для CONFIRMED.
const canReschedule = (s: AppointmentStatus) => s === AppointmentStatus.CONFIRMED;
const canReview = (a: AppointmentResponseDto) => a.status === AppointmentStatus.COMPLETED && !a.review;

interface Props {
  selected: AppointmentResponseDto | null;
  modalLoading: boolean;
  onClose: () => void;
  onQuickConfirm: (id: number) => void;
  onOpenCancel: (appointment: AppointmentResponseDto) => void;
  onOpenReschedule: (appointment: AppointmentResponseDto) => void;
  onOpenReview: (appointment: AppointmentResponseDto) => void;
}

const canConfirm = (a: AppointmentResponseDto) =>
  a.status === AppointmentStatus.PENDING && a.createdBy === 'expert';

export const ClientAppointmentDetailsDialog: React.FC<Props> = ({
  selected,
  modalLoading,
  onClose,
  onQuickConfirm,
  onOpenCancel,
  onOpenReschedule,
  onOpenReview,
}) => {
  const hasActions = selected && (
    canConfirm(selected) || canReschedule(selected.status) || canReview(selected) || canCancel(selected.status)
  );

  return (
    <BaseDialog
      open={selected !== null || modalLoading}
      onClose={onClose}
      title="Деталі запису"
      iDoc={ModalIndices.CLIENT_APPOINTMENT_DETAILS}
      actions={
        hasActions ? (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            {canConfirm(selected!) && (
              <Button size="small" variant="contained" color="primary" onClick={() => onQuickConfirm(selected!.id)}>
                Підтвердити
              </Button>
            )}
            {canReschedule(selected!.status) && (
              <Button size="small" variant="outlined" onClick={() => onOpenReschedule(selected!)}>
                Перенести
              </Button>
            )}
            {canReview(selected!) && (
              <Button size="small" variant="outlined" color="warning" onClick={() => { onClose(); onOpenReview(selected!); }}>
                Залишити відгук
              </Button>
            )}
            {canCancel(selected!.status) && (
              <Button size="small" color="error" onClick={() => onOpenCancel(selected!)}>
                Скасувати
              </Button>
            )}
          </Box>
        ) : undefined
      }
    >
      {modalLoading || !selected ? (
        <Box display="flex" justifyContent="center" sx={{ py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UserAvatar path={selected.expert.avatar} name={selected.expert.name} sx={{ width: 44, height: 44 }} />
              <Typography variant="h6">{selected.expert.name}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Дата</Typography>
            <Typography variant="body2">{new Date(selected.scheduledAt).toLocaleString('uk-UA')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Тривалість</Typography>
            <Typography variant="body2">{selected.durationMinutes} хв.</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Статус</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Chip
                label={getClientStatusLabel(selected)}
                color={STATUS_COLORS[selected.status]}
                size="small"
                variant="outlined"
              />
              <AppointmentCountdown scheduledAt={selected.scheduledAt} status={selected.status} />
            </Box>
          </Grid>
          {selected.clientNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Ваші нотатки</Typography>
              <Typography variant="body2">{selected.clientNotes}</Typography>
            </Grid>
          )}
          {selected.expertNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Нотатки експерта</Typography>
              <Typography variant="body2">{selected.expertNotes}</Typography>
            </Grid>
          )}
          {selected.outcome && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Підсумок зустрічі</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.outcome}</Typography>
            </Grid>
          )}
          {selected.cancellationReason && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Причина скасування</Typography>
              <Typography variant="body2">{selected.cancellationReason}</Typography>
            </Grid>
          )}
          {selected.review && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Ваш відгук</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Rating value={selected.review.rating} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">{selected.review.rating}/5</Typography>
              </Box>
              {selected.review.comment && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>{selected.review.comment}</Typography>
              )}
            </Grid>
          )}
        </Grid>
      )}
    </BaseDialog>
  );
};
